import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { useAskInputSearchModes } from '#pplx/frontend/apps/ask/ask-input/ask-input-attributions/hooks/useAskInputSearchModes.js';
import { useComputerCommandMenuCatalogImplementation } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/computerCommandMenuCatalogImplementationShared.js';
import { isComputerMenuItemAllowed } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/computerMenuModel.js';
import { runAfterCurrentSelectionEvent } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerCommandMenuActions.js';
import { useComputerCommandMenuData } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerCommandMenuData.js';
import { useComputerCommandMenuSelection } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerCommandMenuSelection.js';
import { useComputerCommandMenuUiActionSelect } from '#pplx/frontend/apps/ask/ask-input/ask-input-computer-command-menu/useComputerCommandMenuUiActionSelect.js';
import { useAskInputActions } from '#pplx/frontend/apps/ask/ask-input/ask-input-provider/components/AskInputProviderShared.js';
import { selectComputerCommandMenuItem } from '#pplx/frontend/apps/ask/composer-sdk-plugins/computer-command-menu/computerCommandMenuSelection.js';
import { useSkillsAndCommandsPlugin } from '#pplx/frontend/apps/ask/composer-sdk-plugins/skills-and-commands/useSkillsAndCommandsPlugin.js';
import { useOpenConnectorModalBySourceId } from '#pplx/frontend/apps/ask/connectors/modal-navigation/useOpenConnectorModalBySourceId.js';
import { useIsIncognito } from '#pplx/frontend/apps/ask/incognito/useIsIncognito.js';
import { getComposerSkillMention } from '#pplx/frontend/apps/ask/skills/getComposerSkillMention.js';
import { useSkillModal } from '#pplx/frontend/apps/ask/skills/hooks/useSkillModal.js';
import { PLUGIN_ICON } from '#pplx/frontend/apps/ask/skills/utils/getMenuIcon.js';
import { getPluginMentionURL } from '#pplx/frontend/apps/ask/skills/utils/getPluginMentionURL.js';
import { useControllableState } from '#pplx/frontend/common/hooks/useControllableState.js';
import { assertNever, assertUnreachableValue, } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
const useCloudCatalogOnly = () => null;
/** Binds the Composer editor to the shared Computer catalog and selection controllers. */
export function useAskComputerCommandMenu({ collectionUuid, computerCommandMenuOpen, enabled, isFollowUp = false, onComputerCommandMenuOpenChange, onSearchModeSelected, selectSearchModeOverride, onSideChatCommandSelect, overlayPlacement = 'above', searchModeControls, searchModeOverride, sideChatAvailable, }) {
    const intl = useIntl();
    const [menuOpen, onMenuOpenChange] = useControllableState({
        value: computerCommandMenuOpen,
        defaultValue: false,
        onChange: onComputerCommandMenuOpenChange,
    });
    const { track } = useInstrumentation();
    const { openModal } = useModalContext();
    const openConnectorModal = useOpenConnectorModalBySourceId({
        reason: 'skill-modal-connector',
        openModal,
    });
    const openSkillModal = useSkillModal({ openModal, openConnectorModal });
    const onSkillDetailsOpen = useCallback((skill) => {
        onMenuOpenChange(false);
        openSkillModal({ skillId: skill.id, context: 'chat-thread' }, { replace: false });
    }, [onMenuOpenChange, openSkillModal]);
    const { isIncognito, isAlwaysIncognitoRequired } = useIsIncognito({
        reason: 'computer-command-menu',
    });
    const [searchQuery, setSearchQuery] = useState('');
    const catalogParams = {
        enabled,
        searchQuery,
        collectionUuid,
        sideChatAvailable,
        intl,
        track,
        isIncognito,
        isFollowUp,
        isAlwaysIncognitoRequired,
        hideCommandItems: false,
        hideSettingsAction: false,
    };
    const useHostCatalog = useComputerCommandMenuCatalogImplementation() ?? useCloudCatalogOnly;
    const hostCatalog = useHostCatalog({ ...catalogParams, open: menuOpen });
    // The cloud hook debounces whatever it receives, so it never sees a host query:
    // switching back to the cloud catalog would otherwise request the retained text.
    const cloudData = useComputerCommandMenuData({
        ...catalogParams,
        enabled: enabled && hostCatalog === null,
        searchQuery: hostCatalog === null ? searchQuery : '',
    });
    const { presentation, resolvedSearchQuery, pagination } = hostCatalog?.data ?? cloudData;
    const emptyMessage = getEmptyMessage({
        emptyState: presentation.emptyState,
        hasNextPage: pagination.hasNextPage,
        intl,
    });
    const getMenuData = useCallback((query) => {
        // The SDK requests results outside render and refreshes when these rows change.
        // Skip the unchanged query so that refresh does not schedule another state update.
        if (query !== searchQuery)
            setSearchQuery(query);
        return {
            presentation: {
                orderedItems: presentation.orderedItems,
                commandItemsFirst: presentation.commandItemsFirst,
                status: presentation.status,
            },
            resolvedSearchQuery,
            emptyMessage,
            pagination,
        };
    }, [
        emptyMessage,
        pagination,
        presentation.orderedItems,
        presentation.commandItemsFirst,
        presentation.status,
        resolvedSearchQuery,
        searchQuery,
    ]);
    const selectedEditorRef = useRef(null);
    const getEditor = useCallback(() => {
        const editor = selectedEditorRef.current;
        if (editor === null)
            return undefined;
        return {
            insertMention: (props) => {
                if (props.variant === 'sources') {
                    editor.insertToken({
                        kind: 'mention',
                        variant: 'sources',
                        id: props.uuid,
                        text: props.text,
                        icon: props.icon,
                    });
                    return;
                }
                editor.insertToken({
                    kind: 'command',
                    id: props.uuid,
                    text: props.text,
                    queryText: props.queryText ?? '',
                    submittedLabel: props.slashCommandSubmittedLabel ?? props.text,
                    icon: props.icon?.type === 'source' ? undefined : props.icon,
                });
            },
            insertPluginMention: (plugin, options) => editor.insertToken({
                kind: 'mention',
                variant: 'plugin',
                id: plugin.id,
                text: plugin.name,
                localizedName: plugin.displayName || undefined,
                prefixWithSlash: options?.prefixWithSlash,
                url: getPluginMentionURL(plugin.id),
                icon: { type: 'icon', icon: PLUGIN_ICON },
            }),
            clearActiveCommandQuery: editor.deleteActiveTrigger,
        };
    }, []);
    const onSkillSelect = useCallback((skill) => selectedEditorRef.current?.insertToken(getComposerSkillMention({ skill, intl, prefixWithSlash: false })), [intl]);
    const onMenuClose = useCallback(() => {
        // Composer owns the visibility notification; shared selection only resets its query here.
        setSearchQuery('');
    }, []);
    const { onActiveMenuChange } = useAskInputActions();
    const { searchModeControllerProps } = useAskInputSearchModes({
        enabled: searchModeControls.enabled,
        isFollowUp,
        searchMode: searchModeOverride,
        onSearchModeSelect: selectSearchModeOverride,
        omittedSearchModes: searchModeControls.omittedModes,
        onSearchModeSelected,
    });
    const selectUiAction = useComputerCommandMenuUiActionSelect({
        computerCommandMenuSearchQuery: searchQuery,
        isFollowUp,
        onActiveMenuChange,
        onSideChatCommandSelect,
        searchModeControllerProps,
    });
    const skillsProps = useMemo(() => ({ showSkills: true, collectionUuid, onSkillSelect }), [collectionUuid, onSkillSelect]);
    const onUiActionSelect = useCallback((action) => selectUiAction(action, onMenuClose), [onMenuClose, selectUiAction]);
    const { handleItemSelect, getUseButtonTrack } = useComputerCommandMenuSelection({
        getEditor,
        skillsProps,
        onMenuClose,
        openModal,
        isIncognito,
        onUiActionSelect,
        isFollowUp,
        isAlwaysIncognitoRequired,
        resolvedSearchQuery,
        track,
    });
    const onItemSelect = useCallback((item, method, editor) => {
        selectedEditorRef.current = editor;
        const selectShared = () => {
            const outcome = selectComputerCommandMenuItem(item, method, {
                editor,
                isFollowUp,
                isAlwaysIncognitoRequired,
                onSelect: handleItemSelect,
                onUiActionSelect,
                resolvedSearchQuery,
                track,
            });
            if (outcome === 'handoff') {
                // The SDK preserves replacement focus without reporting a close on handoff.
                onMenuOpenChange(false);
            }
            return outcome;
        };
        if (hostCatalog === null)
            return selectShared();
        switch (item.action.type) {
            case 'skill': {
                if (!isComputerMenuItemAllowed(item, isFollowUp, isAlwaysIncognitoRequired))
                    return;
                const mention = hostCatalog.selectSkill(item.action.selectableSkill, method);
                runAfterCurrentSelectionEvent(() => {
                    editor.insertToken(mention);
                    onMenuClose();
                });
                return;
            }
            case 'ui_action': {
                const outcome = selectShared();
                if (isComputerMenuItemAllowed(item, isFollowUp, isAlwaysIncognitoRequired)) {
                    hostCatalog.onUiActionSelected?.(item.action.actionId);
                }
                return outcome;
            }
            case 'slash_command':
            case 'connector':
            case 'plugin':
                return selectShared();
            default:
                return assertNever(item.action);
        }
    }, [
        handleItemSelect,
        hostCatalog,
        isAlwaysIncognitoRequired,
        isFollowUp,
        onMenuClose,
        onMenuOpenChange,
        onUiActionSelect,
        resolvedSearchQuery,
        track,
    ]);
    const getHostUseButtonTrack = useCallback((item) => {
        switch (item.action.type) {
            case 'skill':
                return false;
            case 'ui_action':
            case 'slash_command':
            case 'connector':
            case 'plugin':
                return getUseButtonTrack(item);
            default:
                assertNever(item.action);
                return false;
        }
    }, [getUseButtonTrack]);
    const menu = useSkillsAndCommandsPlugin({
        showComputerCommandMenu: enabled,
        getMenuData,
        onItemSelect,
        onSkillDetailsOpen: hostCatalog === null ? onSkillDetailsOpen : undefined,
        getUseButtonTrack: hostCatalog === null ? getUseButtonTrack : getHostUseButtonTrack,
        computerCommandMenuOpen: menuOpen,
        onComputerCommandMenuOpenChange: onMenuOpenChange,
    });
    return useMemo(() => ({
        ...menu,
        plugins: enabled ? menu.plugins : [],
        typeahead: enabled
            ? {
                ...menu.typeahead,
                overlayPlacement,
                commandMenu: menu.typeahead?.commandMenu && {
                    ...menu.typeahead.commandMenu,
                    refreshStatus: 'screenReaderOnly',
                    emptyState: 'spacious',
                    pendingContinuation: 'hidden',
                },
            }
            : undefined,
    }), [enabled, menu, overlayPlacement]);
}
function getEmptyMessage({ emptyState, hasNextPage, intl, }) {
    switch (emptyState) {
        case 'none':
            return undefined;
        case 'pending':
        case 'confirmed':
            return hasNextPage
                ? undefined
                : intl.formatMessage({
                    defaultMessage: 'No skills found',
                    description: 'Message shown when skill search returns no results',
                    id: '8df496752a',
                });
        default:
            return assertUnreachableValue(emptyState);
    }
}
//# sourceMappingURL=useAskComputerCommandMenu.js.map